import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useQuery } from 'react-query';
import { generatePath, useNavigate } from 'react-router-dom';
import routes from '../../../Common/consts/routes';
import { getProjectDetails, leaveProject, removeProject } from '../api/projectDetailsHttpClient';
import useProjectUserPermissions from './useProjectUserPermissions';

export type ProjectFinalizeFormType = {
    toolProjectId: string;
    toolProjectDeeplink: string;
    toolId: string;
    capabilityId: string;
};

const useProjectDetails = (id: string) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isRemoving, setIsRemoving] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const navigate = useNavigate();
    const getProjectDetailsCallback = useCallback(() => getProjectDetails(id), [id]);

    const { data: projectDetails, isLoading: isLoadingProjectDetails } = useQuery(
        ['project_details', id],
        getProjectDetailsCallback,
        {
            onError: (error) => {
                if (axios.isAxiosError(error) && error.response?.status === 401) {
                    enqueueSnackbar("You don't have access to this project", {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    });
                }
            },
        },
    );

    const { data: permissions, isLoading: isLoadingPermissions } = useProjectUserPermissions(id);

    const handleRemove = async () => {
        setIsRemoving(true);
        try {
            await removeProject(id);

            enqueueSnackbar('Project has been removed', {
                variant: 'success',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });

            navigate(
                generatePath(routes.projects, {
                    tab: 'my',
                }),
            );
        } catch {
            enqueueSnackbar('Something went wrong', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } finally {
            setIsRemoving(false);
        }
    };

    const handleLeave = async () => {
        setIsLeaving(true);
        try {
            await leaveProject(id);

            enqueueSnackbar('You have left the project', {
                variant: 'success',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });

            navigate(
                generatePath(routes.projects, {
                    tab: 'my',
                }),
            );
        } catch {
            enqueueSnackbar('Something went wrong', {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
            });
        } finally {
            setIsLeaving(false);
        }
    };

    return {
        projectDetails,
        permissions,
        handleRemove,
        handleLeave,
        isLoading: isLoadingProjectDetails || isLoadingPermissions || isRemoving || isLeaving,
    };
};

export default useProjectDetails;
